// src/components/takeout/TakeoutOrdersPanel.js
// 外帶訂單 tab（Plan 106 §1.2 定案）— 掛在「訂單管理」console 第二個 tab
//
//   列表：代號／取餐時間／客人大名（粗體＋明細 ▸）／電話／金額／後五碼／狀態 chip／操作
//   點列 → 右側明細抽屜（比照 CallLogPage Drawer anchor=right 460px；內文 ≥20px）：
//   代號大字＋狀態、取餐時間/手機末三碼核對/客人/後五碼/建單時間、品項明細、合計、
//   備註琥珀塊、底部狀態推進鈕＋取消（僅待核帳）。不做列印。
//
// 資料自管（date/status filter/orders/counts）；Socket 事件由父層 console 轉入
// （refreshSignal 遞增觸發重抓）；counts 回拋給父層畫 tab badge。
// ============================================================

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box, Paper, Typography, Button, IconButton, Drawer, Divider,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Tooltip, Stack, Skeleton, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import TakeoutDiningIcon from '@mui/icons-material/TakeoutDining';

import SingleDateNavigator from '../dashboard/SingleDateNavigator';
import takeoutService from '../../services/takeoutService';

const BLUE = '#145da0';
const FAR_FUTURE = '2099-12-31';

const todayYmd = () => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Taipei', year: 'numeric', month: '2-digit', day: '2-digit'
}).format(new Date());

const money = (n) => `NT$${Number(n || 0).toLocaleString()}`;
const fmtTime = (t) => String(t || '').slice(0, 5);
// 建單時間（timestamptz → 台北 HH:MM；非當日帶日期）
const fmtCreatedAt = (iso, listDate) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const ymd = d.toLocaleDateString('en-CA', { timeZone: 'Asia/Taipei' });
  const hm = d.toLocaleTimeString('en-GB', { timeZone: 'Asia/Taipei', hour: '2-digit', minute: '2-digit', hour12: false });
  if (ymd === listDate) return `今天 ${hm}`;
  const [, m, day] = ymd.split('-');
  return `${parseInt(m, 10)}/${parseInt(day, 10)} ${hm}`;
};

const STATUS = {
  reported:  { label: '待核帳', bg: '#fef3c7', fg: '#92400e', action: '✓ 確認收款', next: 'confirmed' },
  confirmed: { label: '準備中', bg: '#dbeafe', fg: '#1d4ed8', action: '可取餐', next: 'ready' },
  ready:     { label: '可取餐', bg: '#dcfce7', fg: '#15803d', action: '完成取餐', next: 'picked_up' },
  picked_up: { label: '已取餐', bg: '#e2e8f0', fg: '#64748b', action: null, next: null },
  cancelled: { label: '已取消', bg: '#fee2e2', fg: '#b91c1c', action: null, next: null },
};
const FILTERS = [
  { key: '', label: '全部' },
  { key: 'reported', label: '待核帳' },
  { key: 'confirmed', label: '準備中' },
  { key: 'ready', label: '可取餐' },
  { key: 'picked_up', label: '已完成' },
];

const StatusChip = ({ status, size = 12, labelOverride }) => {
  const st = STATUS[status] || { label: status, bg: '#eee', fg: '#666' };
  return (
    <Box component="span" sx={{
      display: 'inline-block', px: 1.25, py: '3px', borderRadius: '999px',
      fontSize: size, fontWeight: 700, bgcolor: st.bg, color: st.fg, whiteSpace: 'nowrap',
    }}>
      {labelOverride || st.label}
    </Box>
  );
};

const headerSx = { bgcolor: '#ddd', fontWeight: 'bold', fontSize: '14px', position: 'sticky', top: 0, zIndex: 2, borderBottom: '1px solid #ccc' };
const cellSx = { py: 1.6, fontSize: 16 };

// 抽屜資訊格（label 13px / 值 22px 粗體 — 106 §1.2 字體 ≥20px）
const DrawerKV = ({ label, value, mono }) => (
  <Box>
    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#94a3b8', mb: 0.25 }}>{label}</Typography>
    <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.25, ...(mono ? { fontFamily: 'monospace', letterSpacing: 2 } : {}) }}>
      {value}
    </Typography>
  </Box>
);

const TakeoutOrdersPanel = ({ refreshSignal = 0, onCountsChange, showSnack }) => {
  const [date, setDate] = useState(todayYmd());
  const [statusFilter, setStatusFilter] = useState('');
  const [orders, setOrders] = useState([]);
  const [counts, setCounts] = useState({ reported: 0, confirmed: 0, ready: 0, picked_up: 0, cancelled: 0 });
  const [loading, setLoading] = useState(false);
  const [drawerOrder, setDrawerOrder] = useState(null);   // 開啟明細抽屜的訂單
  const [cancelTarget, setCancelTarget] = useState(null); // 取消二次確認
  const onCountsRef = useRef(onCountsChange);
  onCountsRef.current = onCountsChange;

  const fetchOrders = useCallback(async (targetDate, status) => {
    setLoading(true);
    try {
      const body = await takeoutService.getOrders({ date: targetDate, status: status || undefined });
      if (body?.success) {
        setOrders(body.orders || []);
        const c = body.counts || {};
        setCounts(c);
        if (onCountsRef.current) onCountsRef.current(c);
        // 抽屜開著時同步最新狀態（Socket 進單/他人操作）
        setDrawerOrder((cur) => cur ? (body.orders || []).find((o) => o.id === cur.id) || cur : cur);
      } else if (showSnack) {
        showSnack('error', body?.error || '取得外帶訂單失敗');
      }
    } catch (e) {
      if (showSnack) showSnack('error', '取得外帶訂單失敗');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchOrders(date, statusFilter); }, [date, statusFilter, refreshSignal, fetchOrders]);

  const advance = async (order) => {
    const st = STATUS[order.status];
    if (!st?.next) return;
    try {
      const body = await takeoutService.updateStatus(order.id, st.next);
      if (body?.success) {
        if (showSnack) showSnack('success', `${order.orderNo} → ${STATUS[st.next].label}`);
        fetchOrders(date, statusFilter);
      } else if (showSnack) {
        showSnack('error', body?.message || '更新狀態失敗');
      }
    } catch (e) {
      if (showSnack) showSnack('error', e?.response?.data?.message || '更新狀態失敗');
    }
  };

  const confirmCancel = async () => {
    const order = cancelTarget;
    setCancelTarget(null);
    if (!order) return;
    try {
      const body = await takeoutService.updateStatus(order.id, 'cancelled');
      if (body?.success) {
        if (showSnack) showSnack('success', `已取消 ${order.orderNo}`);
        setDrawerOrder(null);
        fetchOrders(date, statusFilter);
      } else if (showSnack) {
        showSnack('error', body?.message || '取消失敗');
      }
    } catch (e) {
      if (showSnack) showSnack('error', e?.response?.data?.message || '取消失敗');
    }
  };

  const itemCount = (o) => (o.items || []).reduce((s, l) => s + (l.qty || 0), 0);
  const phoneLast3 = (o) => String(o.customerPhone || '').replace(/\D/g, '').slice(-3) || '—';
  // 無後五碼＝取餐時付款（bankTransferEnabled=false 的店家）；新訂單免核帳，文案切「新訂單／確認接單」
  const isOnPickup = (o) => !o.paymentLast5;

  const activeCount = useMemo(
    () => (counts.reported || 0) + (counts.confirmed || 0) + (counts.ready || 0),
    [counts]
  );

  const renderSkeletonRows = (count = 6) => (
    Array.from({ length: count }).map((_, i) => (
      <TableRow key={`sk-${i}`}>
        {Array.from({ length: 8 }).map((__, j) => (
          <TableCell key={j} sx={cellSx}><Skeleton variant="text" /></TableCell>
        ))}
      </TableRow>
    ))
  );

  const drawerSt = drawerOrder ? STATUS[drawerOrder.status] : null;
  const drawerOnPickup = drawerOrder ? isOnPickup(drawerOrder) : false;
  const drawerNewOrder = !!drawerOrder && drawerOrder.status === 'reported' && drawerOnPickup;
  const drawerStLabel = drawerNewOrder ? '新訂單' : undefined;         // undefined → StatusChip 用預設 label
  const drawerStAction = drawerNewOrder ? '✓ 確認接單' : drawerSt?.action;

  return (
    <>
      {/* Action row：狀態篩選 chips（左）＋ 重新整理 ＋ 日期（右） */}
      <Box sx={{
        flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1.5,
        px: 3, py: 1.5, bgcolor: '#f9f9f9', borderBottom: '1px solid #f0f0f0',
      }}>
        <Stack direction="row" spacing={0.75}>
          {FILTERS.map((f) => {
            const on = statusFilter === f.key;
            const count = f.key ? (counts[f.key] || 0) : null;
            return (
              <Box key={f.key || 'all'} onClick={() => setStatusFilter(f.key)}
                sx={{
                  px: 1.5, py: '5px', borderRadius: '999px', cursor: 'pointer', fontSize: 13,
                  fontWeight: on ? 700 : 500, border: '1px solid',
                  borderColor: on ? BLUE : '#d5dbe3',
                  bgcolor: on ? BLUE : 'white', color: on ? 'white' : '#546e7a',
                  '&:hover': { borderColor: BLUE },
                }}>
                {f.label}{count !== null && count > 0 ? ` ${count}` : ''}
              </Box>
            );
          })}
        </Stack>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="重新整理">
          <IconButton onClick={() => fetchOrders(date, statusFilter)} disabled={loading} size="small"
            sx={{ borderRadius: '6px', bgcolor: 'rgba(20,93,160,0.06)', '&:hover': { bgcolor: 'rgba(20,93,160,0.14)' } }}>
            <RefreshIcon fontSize="small" sx={{ color: BLUE }} />
          </IconButton>
        </Tooltip>
        <SingleDateNavigator value={date} onChange={setDate} maxDate={FAR_FUTURE} />
      </Box>

      {/* 訂單表 */}
      <Box sx={{
        flex: 1, overflow: 'auto', px: 3,
        '&::-webkit-scrollbar': { width: '6px' },
        '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.2)', borderRadius: '3px' },
      }}>
        <TableContainer component={Paper} sx={{ mt: 2, mb: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.06)', maxHeight: 'calc(100vh - 260px)', overflow: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={headerSx}>代號</TableCell>
                <TableCell sx={headerSx}>取餐時間</TableCell>
                <TableCell sx={headerSx}>客人</TableCell>
                <TableCell sx={headerSx}>電話</TableCell>
                <TableCell sx={headerSx} align="right">金額</TableCell>
                <TableCell sx={headerSx}>後五碼</TableCell>
                <TableCell sx={headerSx}>狀態</TableCell>
                <TableCell sx={headerSx}>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && orders.length === 0 && renderSkeletonRows()}
              {!loading && orders.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6, color: '#999' }}>
                    <TakeoutDiningIcon sx={{ fontSize: 48, color: '#ddd', mb: 1, display: 'block', mx: 'auto' }} />
                    <Typography>
                      {date === todayYmd() ? '今天沒有外帶訂單' : '此日期沒有外帶訂單'}
                      {statusFilter ? '（此狀態）' : ''}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {orders.map((o) => {
                const st = STATUS[o.status] || {};
                const onPickup = isOnPickup(o);
                const newOrder = o.status === 'reported' && onPickup;   // 取餐付新單：免核帳
                const stLabel = newOrder ? '新訂單' : undefined;        // undefined → StatusChip 用預設 label
                const stAction = newOrder ? '✓ 確認接單' : st.action;
                const dim = o.status === 'picked_up' || o.status === 'cancelled';
                return (
                  <TableRow key={o.id} onClick={() => setDrawerOrder(o)}
                    sx={{ cursor: 'pointer', opacity: dim ? 0.55 : 1, '&:hover': { bgcolor: '#f0f4ff !important' } }}>
                    <TableCell sx={{ ...cellSx, fontWeight: 800, fontSize: 17 }}>{o.orderNo}</TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 700 }}>{fmtTime(o.pickupTime)}</TableCell>
                    <TableCell sx={cellSx}>
                      <Typography component="span" sx={{ fontSize: 16, fontWeight: 700 }}>{o.customerName}</Typography>
                      <Typography component="span" sx={{ fontSize: 12.5, fontWeight: 700, color: BLUE, ml: 0.75, whiteSpace: 'nowrap' }}>明細 ▸</Typography>
                    </TableCell>
                    <TableCell sx={{ ...cellSx, fontFamily: 'monospace' }}>{o.customerPhone}</TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 700 }} align="right">{money(o.total)}</TableCell>
                    <TableCell sx={{ ...cellSx, fontFamily: 'monospace', fontWeight: 700, letterSpacing: 1.5 }}>
                      {onPickup
                        ? <Typography component="span" sx={{ fontSize: 12.5, color: '#64748b' }}>取餐付</Typography>
                        : o.paymentLast5}
                    </TableCell>
                    <TableCell sx={cellSx}><StatusChip status={o.status} labelOverride={stLabel} /></TableCell>
                    <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                      {stAction && (
                        <Button size="small" variant="contained" onClick={(e) => { e.stopPropagation(); advance(o); }}
                          sx={{ bgcolor: BLUE, '&:hover': { bgcolor: '#0e447a' }, textTransform: 'none', fontWeight: 700, fontSize: 12.5, px: 1.25, py: 0.5 }}>
                          {stAction}
                        </Button>
                      )}
                      {o.status === 'reported' && (
                        <Button size="small" onClick={(e) => { e.stopPropagation(); setCancelTarget(o); }}
                          sx={{ color: '#d32f2f', fontWeight: 700, fontSize: 12.5, minWidth: 0, ml: 0.75 }}>
                          取消
                        </Button>
                      )}
                      {!stAction && o.status !== 'reported' && <Typography component="span" sx={{ color: '#ccc' }}>—</Typography>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography sx={{ fontSize: 12, color: '#999', pb: 2 }}>
          狀態流：待核帳（客人已回報後五碼，請對帳）→ 準備中 → 可取餐 → 已取餐。對不到帳請來電客人確認，必要時取消。
          目前未完成 {activeCount} 筆。
        </Typography>
      </Box>

      {/* ═══ 右側明細抽屜（比照 CallLogPage；字體 ≥20px）═══ */}
      <Drawer anchor="right" open={!!drawerOrder} onClose={() => setDrawerOrder(null)}
        PaperProps={{ sx: { width: 460, maxWidth: '94vw' } }}>
        {drawerOrder && (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* header：代號大字 + 狀態 chip */}
            <Box sx={{
              px: 2.5, py: 2, borderBottom: '1px solid #e0e0e0', bgcolor: '#f6f9fc',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
            }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography sx={{ fontSize: 30, fontWeight: 900, letterSpacing: 1 }}>{drawerOrder.orderNo}</Typography>
                <StatusChip status={drawerOrder.status} size={14} labelOverride={drawerStLabel} />
              </Stack>
              <IconButton onClick={() => setDrawerOrder(null)}><CloseIcon /></IconButton>
            </Box>

            {/* body */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 2.25, columnGap: 1 }}>
                <DrawerKV label="取餐時間" value={fmtTime(drawerOrder.pickupTime)} />
                <DrawerKV label="取餐核對（手機末三碼）" value={phoneLast3(drawerOrder)} mono />
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#94a3b8', mb: 0.25 }}>客人</Typography>
                  <Typography sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.25 }}>{drawerOrder.customerName}</Typography>
                  <Typography sx={{ fontSize: 16, color: '#64748b', fontFamily: 'monospace' }}>{drawerOrder.customerPhone}</Typography>
                </Box>
                {drawerOnPickup
                  ? <DrawerKV label="匯款後五碼" value="取餐時付款" />
                  : <DrawerKV label="匯款後五碼" value={drawerOrder.paymentLast5} mono />}
                <DrawerKV label="建單時間" value={fmtCreatedAt(drawerOrder.createdAt, date)} />
              </Box>

              {/* 品項明細（廚房備餐看這裡；每行 ≥20px） */}
              <Divider sx={{ my: 2 }} />
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#94a3b8', mb: 0.5 }}>
                品項明細（共 {itemCount(drawerOrder)} 件）
              </Typography>
              {(drawerOrder.items || []).map((l, i) => (
                <Stack key={i} direction="row" justifyContent="space-between" alignItems="baseline" spacing={1.5}
                  sx={{ py: 1, borderBottom: '1px dashed #e2e8f0' }}>
                  <Typography sx={{ fontSize: 20, lineHeight: 1.35 }}>
                    {l.itemName}{l.variantLabel ? `（${l.variantLabel}）` : ''}
                    {l.options?.length ? <Box component="span" sx={{ fontSize: 16, color: '#64748b' }}>＋{l.options.map((op) => op.label).join('、')}</Box> : null}
                    <Box component="span" sx={{ color: BLUE, fontWeight: 800, whiteSpace: 'nowrap' }}> × {l.qty}</Box>
                  </Typography>
                  <Typography sx={{ fontSize: 18, color: '#64748b', whiteSpace: 'nowrap' }}>{money(l.lineTotal)}</Typography>
                </Stack>
              ))}
              <Stack direction="row" justifyContent="space-between" sx={{ py: 1.5 }}>
                <Typography sx={{ fontSize: 22, fontWeight: 800 }}>合計</Typography>
                <Typography sx={{ fontSize: 22, fontWeight: 800 }}>{money(drawerOrder.total)}</Typography>
              </Stack>

              {drawerOrder.customerNote && (
                <Box sx={{ bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: 2, px: 2, py: 1.5 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#b45309', mb: 0.25 }}>備註</Typography>
                  <Typography sx={{ fontSize: 20, color: '#78350f', lineHeight: 1.5 }}>{drawerOrder.customerNote}</Typography>
                </Box>
              )}
            </Box>

            {/* footer：狀態推進鈕（大顆）＋ 取消（僅待核帳） */}
            <Box sx={{ px: 2.5, py: 2, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 1, flexShrink: 0 }}>
              {drawerStAction ? (
                <Button fullWidth variant="contained" onClick={() => advance(drawerOrder)}
                  sx={{ bgcolor: BLUE, '&:hover': { bgcolor: '#0e447a' }, py: 1.4, fontSize: 18, fontWeight: 700, textTransform: 'none', borderRadius: 2 }}>
                  {drawerStAction}
                </Button>
              ) : (
                <Button fullWidth disabled sx={{ py: 1.4, fontSize: 18, fontWeight: 700, borderRadius: 2, bgcolor: '#f1f5f9' }}>
                  {drawerSt?.label}
                </Button>
              )}
              {drawerOrder.status === 'reported' && (
                <Button onClick={() => setCancelTarget(drawerOrder)}
                  sx={{ px: 3, color: '#d32f2f', border: '1px solid #fecaca', borderRadius: 2, fontWeight: 700, fontSize: 16, whiteSpace: 'nowrap', flexShrink: 0 }}>
                  取消
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Drawer>

      {/* 取消二次確認 */}
      <Dialog open={!!cancelTarget} onClose={() => setCancelTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>取消外帶訂單</DialogTitle>
        <DialogContent>
          <Typography>
            確定取消 <b>{cancelTarget?.orderNo}</b>（{cancelTarget?.customerName}，{money(cancelTarget?.total)}）？
            若客人已完成匯款，請先來電確認退款事宜。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelTarget(null)}>返回</Button>
          <Button color="error" variant="contained" onClick={confirmCancel}>確定取消訂單</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TakeoutOrdersPanel;
